// Heap / Priority Queue — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as tries.js: concept → internals →
// the pattern's sub-variants → errors → complexity → beginner drills → cheatsheet → playground.

import heapDrills from './heapDrills';
const drill = id => heapDrills.find(d => d.id === id);

const heap = {
  key: 'Heap / Priority Queue',
  tagline: 'Never sort the whole line — just always know who\'s at the front of it.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Heaps',
      type: 'text',
      body: [
        "A heap is a complete binary tree with one ordering rule: in a MIN-heap, every parent is smaller than or equal to both of its children (so the smallest value in the entire structure is always sitting at the root). A MAX-heap flips that rule — every parent is bigger, so the largest value sits at the root.",
        "\"Complete\" here has a specific meaning: every level of the tree is completely filled except possibly the last, and the last level fills strictly left to right with no gaps. That completeness is what lets a heap be stored as a plain array with no pointers at all — the tree shape is entirely implicit in the array's indices.",
        "A heap does NOT keep everything sorted — only the root is guaranteed to be the min (or max) at any moment. That's a deliberate trade: giving up full ordering everywhere in exchange for O(log n) insert and O(log n) removal of the best element, repeatedly, without ever re-sorting the whole thing.",
      ],
    },
    {
      id: 'why',
      title: 'Why Heaps?',
      type: 'text',
      body: [
        "A huge number of problems only ever need to know \"what's the best (smallest/largest/closest/most frequent) thing available right now\" — not a fully sorted view of everything. Sorting the entire collection every time that question comes up is massive overkill: a heap answers it in O(1) to peek, and O(log n) to update after removing or adding an item.",
        "This shows up constantly: always process the most urgent task next, always merge in the smallest remaining value next, always know the median of a growing stream of numbers, always track the k best candidates seen so far without holding onto everything else. In every one of these, re-sorting from scratch on every single update would be needlessly expensive — a heap keeps exactly the running answer accessible, cheaply, as the data changes.",
      ],
    },
    {
      id: 'analogy',
      title: 'How Heaps Work — An Emergency Room Triage Desk',
      type: 'text',
      heading: 'Never Re-Sort the Waiting Room — Always Know Who\'s Most Urgent',
      body: [
        "Picture a hospital emergency room. Patients don't get treated in the order they arrived — they get treated by severity, most critical first. But the triage nurse doesn't re-sort the entire waiting room into a perfect line every time a new patient walks in, or every time someone gets called back; that would mean re-examining and re-ranking every single person present, for every single arrival.",
        "Instead, the desk only ever needs to know one thing precisely: who's the MOST urgent right now. When a new patient arrives, they're slotted in relative to just a few others (not compared against the whole room), and when someone is called back, whoever's now most urgent among those remaining rises to the front — again without touching most of the room. That's exactly a heap: no full ordering is ever maintained, only \"who's currently at the top\" is kept cheap to find and cheap to update.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing a Heap as a Tree',
      type: 'tree-diagram',
      body: [
        "A heap is conceptually a binary tree — this is the picture worth holding in your head, even though it's actually stored as a flat array (the next section shows exactly how). Every parent here is smaller than or equal to both of its children, all the way down: that's the entire min-heap invariant, checked locally at every single node.",
        "Notice that a heap is NOT sorted left-to-right or in any total order — 7 and 2 aren't directly compared to each other at all, since neither is the other's parent. Only the root-to-leaf relationship is guaranteed.",
      ],
      demo: {
        tree: {
          val: 1,
          left: { val: 3, left: { val: 7, left: null, right: null }, right: { val: 4, left: null, right: null } },
          right: { val: 2, left: { val: 8, left: null, right: null }, right: { val: 9, left: null, right: null } },
        },
        note: 'Every parent ≤ both children — that\'s the whole invariant. The root, 1, is guaranteed to be the smallest value anywhere in the structure.',
      },
    },
    {
      id: 'arraybacking',
      title: 'Why a Heap Is Stored as a Plain Array',
      type: 'code',
      code:
`// The exact same heap as the diagram above, stored as one flat array:
int[] heap = { 1, 3, 2, 7, 4, 8, 9 };
//              0  1  2  3  4  5  6   <- index

// For any index i:
int parent = (i - 1) / 2;
int left   = 2 * i + 1;
int right  = 2 * i + 2;

// Check it: index 1 (value 3) has children at 2*1+1=3 (value 7) and 2*1+2=4 (value 4)`,
      note: 'Because a heap is always a COMPLETE tree (no gaps), this index arithmetic works perfectly — no left/right pointers are ever needed, which is exactly why a heap is dramatically simpler and more memory-efficient to implement than a general binary tree.',
    },
    {
      id: 'declare',
      title: 'Java\'s PriorityQueue',
      type: 'code',
      code:
`// Min-heap (default) — smallest element first
PriorityQueue<Integer> minHeap = new PriorityQueue<>();

// Max-heap — pass a comparator that reverses the natural order
PriorityQueue<Integer> maxHeap = new PriorityQueue<>(Collections.reverseOrder());

// Custom objects need a comparator (or implement Comparable)
PriorityQueue<int[]> byDistance = new PriorityQueue<>(
    (a, b) -> (a[0]*a[0] + a[1]*a[1]) - (b[0]*b[0] + b[1]*b[1])
);

minHeap.add(5);          // insert — O(log n)
int smallest = minHeap.peek();   // look, don't remove — O(1)
int removed = minHeap.poll();    // remove and return the smallest — O(log n)`,
      note: 'Java\'s PriorityQueue IS a heap internally (array-backed, exactly as described above) — it\'s a min-heap by default, which surprises people expecting "priority" to mean "biggest first."',
    },
    {
      id: 'siftup',
      title: 'Insertion: Sift-Up (Bubble Up)',
      type: 'code',
      code:
`void siftUp(int[] heap, int i) {
    while (i > 0 && heap[i] < heap[(i - 1) / 2]) {
        swap(heap, i, (i - 1) / 2);
        i = (i - 1) / 2;
    }
}
// A new element is appended at the next free slot, then this runs
// to restore the heap property along the single path up to the root.`,
      note: 'Only the path from the new leaf up to the root can possibly be out of order — everything else in the heap was already valid, so nothing else needs checking.',
    },
    {
      id: 'siftdown',
      title: 'Removal: Sift-Down (Bubble Down)',
      type: 'code',
      code:
`void siftDown(int[] heap, int i, int size) {
    int smallest = i, left = 2 * i + 1, right = 2 * i + 2;
    if (left < size && heap[left] < heap[smallest]) smallest = left;
    if (right < size && heap[right] < heap[smallest]) smallest = right;
    if (smallest != i) {
        swap(heap, i, smallest);
        siftDown(heap, smallest, size);
    }
}
// The root is removed, the LAST element takes its place, then this
// runs to push it down until the heap property holds again.`,
      note: 'The last element (not an arbitrary one) fills the vacated root, because moving it there is the only move that keeps the tree complete — no gaps anywhere in the array.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Assuming a heap is fully sorted',
          why: 'Only the root is guaranteed to be the min (or max) — iterating a heap\'s underlying array in index order does NOT produce sorted output, a very common wrong assumption.',
          fix: 'To get sorted output from a heap, repeatedly extract the root (poll()) — that produces sorted order one element at a time, at O(n log n) total, exactly like heapsort.',
        },
        {
          mistake: 'Forgetting Java\'s PriorityQueue is a min-heap by default',
          why: 'Calling poll() expecting the largest element (because "priority" sounds like "most important first") silently returns the SMALLEST instead — a frequent, quiet bug.',
          fix: 'Pass Collections.reverseOrder() (or an equivalent comparator) explicitly whenever a max-heap is actually needed.',
        },
        {
          mistake: 'Moving an arbitrary element into the root instead of the last one, after removal',
          why: 'Only the LAST element in the array preserves the tree\'s completeness when moved into the vacated root — moving any other element leaves a gap, breaking the array-to-tree index mapping entirely.',
          fix: 'Always swap the last element into the root position (then shrink the array by one) before sifting down — never any other element.',
        },
        {
          mistake: 'Inserting n elements one at a time when the whole array is known upfront',
          why: 'n individual sift-up insertions cost O(n log n) total — needlessly more than necessary when every element is already available at once.',
          fix: 'Use bottom-up heapify (O(n)) instead when building a heap from a complete, known array — reserve one-at-a-time insertion for genuinely streaming data.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'peek() — look at the root', time: 'O(1)', note: 'No traversal needed — it\'s always at index 0.' },
        { op: 'add() / poll() — insert or remove', time: 'O(log n)', note: 'Sift-up or sift-down travels at most the height of the tree.' },
        { op: 'Build from n elements one at a time', time: 'O(n log n)', note: 'n inserts, each O(log n).' },
        { op: 'Build from n elements via bottom-up heapify', time: 'O(n)', note: 'Surprising but true — most nodes are near the bottom and barely need to move.' },
        { op: 'Full sort by repeated poll() (heapsort)', time: 'O(n log n)', note: 'n removals, each O(log n).' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for a heap when you see…',
      body: [
        'Repeatedly needing the current smallest/largest/best item from a changing collection — "Kth largest," "top k," "closest k."',
        'Merging many already-sorted sources into one — a heap tracks the current-smallest candidate from each source cheaply.',
        'A running statistic over a data STREAM (like a running median) that must stay cheap to update after every new value.',
        'Greedy scheduling where "always do the currently most valuable/urgent thing" is the strategy, possibly with items temporarily unavailable (a cooldown, a dependency).',
        'Avoid it when the FULL sorted order is actually needed up front — just sort once (O(n log n)) rather than paying heap overhead for something a single sort already answers more simply.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('build-min-heap'),
            drill('extract-min'),
            drill('kth-largest-in-array-drill'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('heapify-array'),
            drill('last-stone-weight'),
            drill('k-closest-points-to-origin'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('find-median-drill'),
            drill('task-scheduler'),
            drill('merge-k-sorted-lists'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with sift-up, sift-down, and heapify before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'Min-heap: every parent ≤ its children, smallest at the root. Max-heap: flip the inequality.',
        'Stored as a plain array: parent = (i-1)/2, left = 2i+1, right = 2i+2 — no pointers needed.',
        'peek() is O(1). add()/poll() are O(log n) — sift-up on insert, sift-down on removal.',
        'Building from a known array: bottom-up heapify is O(n), not O(n log n) — start from the last non-leaf node and work back to the root.',
        'A heap is NOT fully sorted — only the root is guaranteed to be the best element at any moment.',
        'Java\'s PriorityQueue is a MIN-heap by default — pass Collections.reverseOrder() for a max-heap.',
        'Merging k sorted sources, or tracking the top/closest k of a stream: a heap of size k is the standard tool, keeping only O(k) candidates in play at once.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default heap;
